import React, { useEffect, useState } from 'react';
import {
    PageContentWrapper,
    LeftContentWrapper,
    RightContentWrapper,
    LeftTopWrapper,
    RightBottomWrapper,
    LeftBottomWrapper,
    VerificationTabsPanelHeader,
} from './Verification.styles';
import { useVesselStore } from '../../context/VesselContext';
import useGetVerificationData from '../../api/useGetVerificationData';
import { ReportMetadata } from '../../types/ViewModel/Verification/ReportMetadata';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExpandPanelButton, TabButton, TabText, TabsWrapper } from '../../styles/button.style';
import { useVerificationStore } from '../../context/VerificationContext';
import usePostUpdateVerificationData from '../../api/usePostUpdateVerificationData';
import DnvResponseListComponent from '../../components/verification/VerificationIssuesList';
import VerificationDetailsComponent from '../../components/verification/VerificationDetails';
import FilterPanel from '../../components/verification/FilterPanel';
import ReportMetadataList from '../../components/verification/ReportMetadataList';
import { canReportBeSentToDnv } from '../../utils/verification/verificationUtils';
import { useToast } from '../../app/useToast';
import { Curtain } from '../../App.styles';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import DownloadReportButtonComponent from '../../components/verification/DownloadReportButton';
import { useAuthStore } from '../../context/AuthContext';
import { VerificationFilters } from '../../types/ViewModel/Verification/VerificationFilters';
import { processVerificationsForReport, buildDefaultFilters } from './VerificationUtils';
import { useVerificationFilterInitialization } from './useVerificationFilterInitialization';

export enum tabEnum {
    details = 1,
    verification = 2,
}

const VerificationComponent: React.FC = () => {
    const { vesselList } = useVesselStore();
    const [selectedTab, setSelectedTab] = useState<tabEnum>(tabEnum.details);
    const { getReportMetadataList } = useGetVerificationData();
    const { sendReport, sendReports } = usePostUpdateVerificationData();
    const [isRightPanelExpanded, setIsRightPanelExpanded] = useState<boolean>(false);
    const { toastError } = useToast();
    const {
        verificationFilter,
        reportMetadataList,
        selectedReportMetadata,
        reportComment,
        populatedReportMetadataList,
        selectedReportMetadataList,
        initialSelectedVessels,
        initialSelectedManagers,
        initialSelectedUsers,

        isDownloadDDExpanded,
        setReportMetadataList,
        setPopulatedReportMetadataList,
        setVerificationFilter,
        setSelectedReportMetadataList,
        setFilteredVerificationVesselList,
        setReportComment,
        setSelectedVessels,
        setSelectedManagers,
        setSelectedYears,
        setSelectedReportMetadata,
        setSelectedReportTypes,
        setSelectedReportStatuses,
        setSelectedUsers,
        setInitialSelectedReportStatuses,
        setSelectedChecks,
        setAvailableChecks,
        setInitialSelectedYears,
        setInitialSelectedReportTypes,
        setInitialSelectedVessels,
        setInitialSelectedManagers,
        setInitialSelectedUsers,
    } = useVerificationStore();
    const { loggedInUser } = useAuthStore();

    const { triggerQueryParamsUpdate, queryParamUpdate } = useQueryParamsHandler();

    useVerificationFilterInitialization({
        vesselList,
        populatedReportMetadataList,
        loggedInUser,
        queryParamUpdate,
        verificationFilter,
        initialSelectedVessels,
        initialSelectedManagers,
        initialSelectedUsers,
        setVerificationFilter,
        setSelectedYears,
        setSelectedUsers,
        setSelectedManagers,
        setSelectedVessels,
        setSelectedReportTypes,
        setSelectedReportStatuses,
        setSelectedChecks,
        setInitialSelectedVessels,
        triggerQueryParamsUpdate,
        setDefaultFilters,
    });

    // Converted to function declaration for hoisting so it can be passed into initialization hook above.
    function setDefaultFilters(verificationFilter: VerificationFilters): VerificationFilters {
        const { updatedFilters, derived } = buildDefaultFilters({
            filters: verificationFilter,
            vesselList: vesselList ?? [],
            populatedReportMetadataList: populatedReportMetadataList ?? [],
        });
        setInitialSelectedReportStatuses(derived.statusesAll);
        setInitialSelectedYears(derived.yearsAll);
        setInitialSelectedReportTypes(derived.typesAll);
        setFilteredVerificationVesselList((vesselList ?? []).filter((v) => populatedReportMetadataList?.some((r) => r.imoNumber === v.imoNumber)));
        setInitialSelectedVessels(derived.vesselsAll);
        setInitialSelectedManagers(derived.managersAll);
        setInitialSelectedUsers(derived.usersAll);
        setAvailableChecks(derived.checksAll);
        return updatedFilters;
    }

    useEffect(() => {
        if (!reportMetadataList) {
            getReportMetadataList();
        }
    }, [reportMetadataList]);

    useEffect(() => {
        if (reportMetadataList && vesselList) {
            const _populatedReportMetadataList = reportMetadataList;

            _populatedReportMetadataList.forEach((r) => {
                r.vesselName = vesselList.find((v) => v.imoNumber === r.imoNumber)?.vesselName;

                if (r.vesselName === '???') {
                    r.vesselName = r.imoNumber.toString();
                }
                r.manager = vesselList.find((v) => v.imoNumber === r.imoNumber)?.technicalOperator;

                r = processVerificationsForReport(r, r.dnvIssues);
            });

            if (!reportComment) {
                setReportComment({ reportId: _populatedReportMetadataList[0].reportId, comment: _populatedReportMetadataList[0].comment });
            }

            setPopulatedReportMetadataList([..._populatedReportMetadataList]);
        }
    }, [reportMetadataList, vesselList]);

    const sendReportToDNV = async (reportMetadata: ReportMetadata) => {
        if (canReportBeSentToDnv(reportMetadata)) {
            const _dnvResponse = await sendReport(reportMetadata);

            if (_dnvResponse?.errors) {
                let errorStr = '';
                _dnvResponse.errors.forEach((d) => (errorStr += d + '\n'));
                toastError(errorStr);
            }
            if (_dnvResponse?.warnings) {
                let errorStr = '';
                _dnvResponse.warnings.forEach((d) => (errorStr += d + '\n'));
                toastError(errorStr);
            }

            if (_dnvResponse == null || _dnvResponse.metadata == null) {
                toastError('No response from DNV API.');
                return;
            }

            const _updatedReportMetadata = _dnvResponse.metadata;
            const _populatedReportMetadataList = populatedReportMetadataList;
            const _reportMetadataIndex = _populatedReportMetadataList.findIndex((r) => r.reportId === _updatedReportMetadata.reportId);

            _updatedReportMetadata.verificationList = _populatedReportMetadataList[_reportMetadataIndex].verificationList;
            _updatedReportMetadata.vesselName = _populatedReportMetadataList[_reportMetadataIndex].vesselName;
            _updatedReportMetadata.manager = _populatedReportMetadataList[_reportMetadataIndex].manager;
            _populatedReportMetadataList[_reportMetadataIndex] = _updatedReportMetadata;

            setPopulatedReportMetadataList([..._populatedReportMetadataList]);
            setSelectedReportMetadata({ ..._populatedReportMetadataList[_reportMetadataIndex] });
        }
    };

    const sendSelectedReportsToDNV = async () => {
        const reportIdArray: number[] = [];
        selectedReportMetadataList.forEach((reportMetadata) => {
            if (canReportBeSentToDnv(reportMetadata)) {
                reportIdArray.push(reportMetadata.reportId);
            }
        });

        if (reportIdArray && reportIdArray.length > 0) {
            const reportIds = reportIdArray.toString();
            const _reportMetadataList = await sendReports(reportIds);

            setReportMetadataList([..._reportMetadataList]);
            setSelectedReportMetadataList([]);
        }
    };

    const checkExpandPanelButton = () => {
        return (
            <ExpandPanelButton top={'50%'} onClick={onToggleExpandRightPanel}>
                {!isRightPanelExpanded && <FontAwesomeIcon icon="chevron-left" />}
                {isRightPanelExpanded && <FontAwesomeIcon icon="chevron-right" />}
            </ExpandPanelButton>
        );
    };

    const onToggleExpandRightPanel = () => {
        if (isRightPanelExpanded) {
            setIsRightPanelExpanded(false);
        } else {
            setIsRightPanelExpanded(true);
        }
    };

    return (
        <PageContentWrapper>
            <LeftContentWrapper isExpanded={isRightPanelExpanded}>
                <LeftTopWrapper>
                    <FilterPanel></FilterPanel>
                </LeftTopWrapper>
                <LeftBottomWrapper>
                    <ReportMetadataList
                        isRightPanelExpanded={isRightPanelExpanded}
                        sendReportToDNV={sendReportToDNV}
                        sendSelectedReportsToDNV={sendSelectedReportsToDNV}
                    ></ReportMetadataList>
                </LeftBottomWrapper>
            </LeftContentWrapper>
            {selectedReportMetadata && (
                <RightContentWrapper isExpanded={isRightPanelExpanded}>
                    {checkExpandPanelButton()}
                    <VerificationTabsPanelHeader columns={'1fr 1fr'}>
                        <TabsWrapper tabs={'125px 125px'}>
                            <TabButton className="first" isSelected={selectedTab === tabEnum.details} onClick={() => setSelectedTab(tabEnum.details)}>
                                <TabText>Details</TabText>
                            </TabButton>
                            <TabButton isSelected={selectedTab === tabEnum.verification} onClick={() => setSelectedTab(tabEnum.verification)}>
                                <TabText>Issues</TabText>
                            </TabButton>
                        </TabsWrapper>

                        <DownloadReportButtonComponent />
                    </VerificationTabsPanelHeader>
                    <RightBottomWrapper>
                        {selectedTab === tabEnum.details && <VerificationDetailsComponent></VerificationDetailsComponent>}
                        {selectedReportMetadata && selectedTab === tabEnum.verification && (
                            <DnvResponseListComponent
                                isExpanded={isRightPanelExpanded}
                                selectedReportMetadata={selectedReportMetadata}
                            ></DnvResponseListComponent>
                        )}
                    </RightBottomWrapper>
                </RightContentWrapper>
            )}
            {isDownloadDDExpanded && <Curtain />}
        </PageContentWrapper>
    );
};
export default VerificationComponent;
